import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CheckCircle, Package, MapPin, Calendar, Clock, Phone, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}/public`);
        setOrder(response.data);
      } catch (error) {
        console.error("Errore caricamento ordine:", error);
        // Prova localStorage come fallback
        const savedOrder = localStorage.getItem(`order_${orderId}`);
        if (savedOrder) {
          setOrder(JSON.parse(savedOrder));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="inline-block w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  const displayOrder = order || {
    order_number: `ORD-${orderId.substring(0, 8).toUpperCase()}`,
    delivery_date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    delivery_time: "10:00-12:00",
    payment_method: "Pagamento alla consegna",
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full" data-testid="order-confirmation-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-4">
            <CheckCircle className="w-12 h-12 text-black" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="confirmation-title">
            Ordine confermato!
          </h1>
          <p className="text-xl text-zinc-300" data-testid="thank-you-message">
            Grazie per il tuo ordine. Riceverai una conferma via email.
          </p>
        </div>

        {/* Order Details */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6" data-testid="order-details">
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-zinc-800">
            <Package className="w-6 h-6 text-green-500" />
            <div>
              <p className="text-sm text-zinc-400">Numero ordine</p>
              <p className="text-xl font-bold text-white" data-testid="order-number">{displayOrder.order_number}</p>
            </div>
          </div>

          {/* Company Order Badge */}
          {displayOrder.delivery_type === "aziendale" && (
            <div className="mb-6 bg-blue-500/10 border border-blue-500/50 rounded-lg p-4">
              <p className="text-blue-400 font-bold text-lg">
                ORDINE AZIENDALE {displayOrder.partner_company_name ? `- ${displayOrder.partner_company_name}` : ""}
              </p>
            </div>
          )}

          {/* Dati Cliente */}
          <div className="mb-6 pb-6 border-b border-zinc-800">
            <h3 className="text-white font-bold mb-3">Dati Cliente</h3>
            <div className="space-y-2 text-sm">
              <p className="text-zinc-300"><span className="text-zinc-500">Nome:</span> {displayOrder.customer_name}</p>
              <p className="text-zinc-300"><span className="text-zinc-500">Telefono:</span> {displayOrder.customer_phone}</p>
              <p className="text-zinc-300"><span className="text-zinc-500">Email:</span> {displayOrder.customer_email}</p>
              {displayOrder.customer_fiscal_code && (
                <p className="text-zinc-300"><span className="text-zinc-500">Codice Fiscale:</span> {displayOrder.customer_fiscal_code}</p>
              )}
              <p className="text-zinc-300"><span className="text-zinc-500">Indirizzo:</span> {displayOrder.customer_address}</p>
              {displayOrder.delivery_comune && (
                <p className="text-zinc-300"><span className="text-zinc-500">Comune:</span> {displayOrder.delivery_comune}</p>
              )}
            </div>
          </div>

          {/* Consegna */}
          <div className="mb-6 pb-6 border-b border-zinc-800">
            <h3 className="text-white font-bold mb-3">Consegna</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <Calendar className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                <div>
                  <p className="text-sm text-zinc-400">Data consegna</p>
                  <p className="text-white font-semibold" data-testid="delivery-date">
                    {new Date(displayOrder.delivery_date).toLocaleDateString('it-IT', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Clock className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                <div>
                  <p className="text-sm text-zinc-400">Fascia oraria</p>
                  <p className="text-white font-semibold" data-testid="delivery-time">{displayOrder.delivery_time}</p>
                </div>
              </div>

              {displayOrder.delivery_notes && (
                <div className="flex gap-3">
                  <MapPin className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-zinc-400">Note consegna</p>
                    <p className="text-white">{displayOrder.delivery_notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Prodotti Ordinati */}
          {displayOrder.items && displayOrder.items.length > 0 && (
            <div className="mb-6 pb-6 border-b border-zinc-800">
              <h3 className="text-white font-bold mb-3">Prodotti Ordinati</h3>
              <div className="space-y-3">
                {displayOrder.items.map((item, index) => (
                  <div key={item.id || item.name} className="flex justify-between items-center text-sm">
                    <div className="flex-1">
                      <p className="text-white">{item.name}</p>
                      <p className="text-zinc-500">Quantità: {item.quantity}</p>
                    </div>
                    <p className="text-white font-semibold">€{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Riepilogo Economico */}
          <div className="mb-6 pb-6 border-b border-zinc-800">
            <h3 className="text-white font-bold mb-3">Riepilogo</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-400">Subtotale</span>
                <span className="text-white">€{(displayOrder.subtotal || 0).toFixed(2)}</span>
              </div>
              {displayOrder.discount > 0 && (
                <div className="flex justify-between text-green-500">
                  <span>Sconto volume</span>
                  <span>-€{displayOrder.discount.toFixed(2)}</span>
                </div>
              )}
              {displayOrder.coupon_discount > 0 && (
                <div className="flex justify-between text-green-500">
                  <span>Codice {displayOrder.coupon_code}</span>
                  <span>-€{displayOrder.coupon_discount.toFixed(2)}</span>
                </div>
              )}
              {displayOrder.business_payment_discount > 0 && (
                <div className="flex justify-between text-green-500">
                  <span>Sconto pagamento anticipato</span>
                  <span>-€{displayOrder.business_payment_discount.toFixed(2)}</span>
                </div>
              )}
              {displayOrder.private_payment_discount > 0 && (
                <div className="flex justify-between text-green-500">
                  <span>Sconto pagamento anticipato (1%)</span>
                  <span>-€{displayOrder.private_payment_discount.toFixed(2)}</span>
                </div>
              )}
              {displayOrder.partner_discount > 0 && (
                <div className="flex justify-between text-blue-400">
                  <span>Sconto aziendale{displayOrder.partner_company_name ? ` (${displayOrder.partner_company_name})` : ""}</span>
                  <span>-€{displayOrder.partner_discount.toFixed(2)}</span>
                </div>
              )}
              {displayOrder.birthday_discount > 0 && (
                <div className="flex justify-between text-pink-500">
                  <span>Sconto compleanno</span>
                  <span>-€{displayOrder.birthday_discount.toFixed(2)}</span>
                </div>
              )}
              {displayOrder.free_product && (
                <div className="flex justify-between text-yellow-500">
                  <span>Omaggio: {displayOrder.free_product}</span>
                  <span>GRATIS</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-zinc-400">Spedizione</span>
                <span className={displayOrder.shipping === 0 ? "text-green-500" : "text-white"}>
                  {displayOrder.shipping === 0 ? "Gratuita" : `€${(displayOrder.shipping || 0).toFixed(2)}`}
                </span>
              </div>
              {displayOrder.iva > 0 && (
                <div className="flex justify-between">
                  <span className="text-zinc-400">IVA (10%)</span>
                  <span className="text-white">€{displayOrder.iva.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-zinc-700 text-lg font-bold">
                <span className="text-white">Totale</span>
                <span className="text-green-500">€{(displayOrder.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Metodo di Pagamento */}
          <div>
            <h3 className="text-white font-bold mb-3">Pagamento</h3>
            <p className="text-white" data-testid="payment-method">{displayOrder.payment_method}</p>
            {displayOrder.points_earned > 0 && (
              <p className="text-green-500 text-sm mt-2">+{displayOrder.points_earned} punti guadagnati!</p>
            )}
          </div>
        </div>

        {/* CONTATTI EMERGENZA - Sezione evidenziata */}
        <div className="bg-yellow-500/20 border-2 border-yellow-500 rounded-lg p-6 mb-6" data-testid="emergency-contacts">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-8 h-8 text-yellow-500 flex-shrink-0" />
            <div>
              <h3 className="text-yellow-500 font-bold text-xl mb-2">Hai un imprevisto?</h3>
              <p className="text-white">
                Se non puoi essere presente alla consegna o hai bisogno di riprogrammare, contattaci subito!
              </p>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <a 
              href="tel:+393928612924" 
              className="flex items-center gap-3 bg-green-500 text-black rounded-lg p-4 font-bold hover:bg-green-400 transition-all"
            >
              <Phone className="w-6 h-6" />
              <div>
                <p className="text-sm opacity-75">Roberto</p>
                <p className="text-lg">392 861 2924</p>
              </div>
            </a>
            <a 
              href="tel:+393342057548" 
              className="flex items-center gap-3 bg-green-500 text-black rounded-lg p-4 font-bold hover:bg-green-400 transition-all"
            >
              <Phone className="w-6 h-6" />
              <div>
                <p className="text-sm opacity-75">Angelo</p>
                <p className="text-lg">334 205 7548</p>
              </div>
            </a>
          </div>
          
          <p className="text-yellow-500/80 text-sm mt-4 text-center">
            Chiamaci o scrivici su WhatsApp per qualsiasi problema con la consegna
          </p>
        </div>

        {/* Important Info */}
        <div className="bg-green-500/10 border border-green-500 rounded-lg p-6 mb-8" data-testid="important-info">
          <h3 className="text-green-500 font-bold text-lg mb-2">Informazioni importanti</h3>
          <ul className="text-green-500 space-y-2 text-sm">
            <li>• I prodotti verranno consegnati refrigerati</li>
            <li>• Conservare immediatamente in frigorifero a +4°C</li>
            <li>• Controlla la data di scadenza su ogni confezione</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate("/catalogo")}
            className="flex-1 bg-green-500 text-black py-4 rounded-lg font-bold hover:bg-green-600 transition-all"
            data-testid="continue-shopping-button"
          >
            Continua gli acquisti
          </button>
          <button
            onClick={() => navigate("/")}
            className="flex-1 bg-zinc-800 text-white py-4 rounded-lg font-bold hover:bg-zinc-700 transition-all"
            data-testid="go-home-button"
          >
            Torna alla home
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrderConfirmationPage;
